import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useExperienceDetail } from '@/hooks/useExperienceDetail';
import { useTranslation } from 'react-i18next';

const REGION_LABEL_KEYS: Record<string, string> = {
  northern: 'experience.regionNorthern',
  central: 'experience.regionCentral',
  southern: 'experience.regionSouthern',
};

export default function ExperienceDetail() {
  const { id } = useParams<{ id: string }>();
  const { experience, loading, error } = useExperienceDetail(id || '');
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <Navbar />
        <main className="pt-16 md:pt-20">
          <div className="mx-auto max-w-[1240px] px-4 md:px-6 pt-4 md:pt-6">
            <div className="aspect-[2/1] rounded-xl bg-background-100 animate-pulse" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !experience) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-16 md:pt-20">
          <div className="mx-auto max-w-[1240px] px-4 md:px-6 py-20 text-center">
            <i className="ri-error-warning-line text-5xl text-secondary-300 mb-4" />
            <h2 className="font-heading text-xl font-bold text-secondary-800 mb-2">{t('experience.notFound')}</h2>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors">
              <i className="ri-arrow-left-line" />{t('experience.backHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <div className="mx-auto max-w-[1240px] px-4 md:px-6 pb-16">
          {/* Breadcrumb */}
          <nav className="py-4" aria-label={t('experience.breadcrumb')}>
            <ol className="flex flex-wrap items-center gap-2 text-[12px] text-secondary-400">
              <li>
                <Link to="/" className="hover:text-primary-600 transition-colors">{t('experience.home')}</Link>
              </li>
              <li><span className="text-secondary-300">&gt;</span></li>
              <li>
                <Link to="/tours" className="hover:text-primary-600 transition-colors">{t('experience.experiences')}</Link>
              </li>
              <li><span className="text-secondary-300">&gt;</span></li>
              <li className="text-secondary-700 line-clamp-1">{experience.title}</li>
            </ol>
          </nav>

          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,860px)_356px]">
            <div>
              {/* Hero image */}
              <div className="relative w-full overflow-hidden rounded-2xl border border-background-200 bg-white shadow-[0_1px_2px_rgba(19,48,44,0.05)]">
                <div className="relative aspect-[2/1] w-full overflow-hidden">
                  <img
                    src={experience.image}
                    alt={experience.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Title card */}
              <div className="mt-5 rounded-2xl border border-background-200 bg-white p-[26px_28px] shadow-[0_1px_2px_rgba(19,48,44,0.05)]">
                <h1 className="text-[22px] md:text-[26px] font-bold leading-[1.3] tracking-[-0.01em] text-foreground-950 mb-3">
                  {experience.title}
                </h1>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-price-tag-3-line" />
                    {experience.category}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-group-line" />
                    {experience.group_type}
                  </span>
                  {experience.region && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-secondary-100 text-secondary-700">
                      <i className="ri-map-pin-line" />
                      {REGION_LABEL_KEYS[experience.region] ? t(REGION_LABEL_KEYS[experience.region]) : experience.region}
                    </span>
                  )}
                </div>
              </div>

              {/* CTA card */}
              <div className="mt-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-background-200 bg-primary-50/50 p-[22px_24px]">
                <p className="max-w-[60ch] text-[13px] leading-[1.6] text-secondary-600">
                  <b className="font-bold text-primary-700">{t('experience.ctaTitle')}</b>{' '}
                  {t('experience.ctaText')}
                </p>
                <Link
                  to={`/contact?tour=${encodeURIComponent(experience.title)}`}
                  className="rounded-[10px] bg-primary-500 px-6 py-3 text-[13.5px] font-bold whitespace-nowrap text-white hover:bg-primary-600 transition-colors inline-flex items-center justify-center"
                >
                  {t('experience.contactExpert')}
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="sticky top-[126px] rounded-2xl border border-background-200 bg-white p-6 shadow-[0_1px_2px_rgba(19,48,44,0.05)]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-secondary-400 mb-1">
                {t('experience.priceFrom')}
              </p>
              <p className="text-[28px] font-extrabold leading-none tracking-[-0.02em] text-primary-600 mb-5">
                {experience.price_from.toLocaleString()} <span className="text-[16px] font-semibold text-secondary-400">USD</span>
              </p>
              <div className="space-y-3 border-t border-background-200/70 pt-4 text-[13px] text-secondary-600">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2">
                    <i className="ri-price-tag-3-line text-secondary-400" />
                    {t('experience.category')}
                  </span>
                  <span className="font-semibold text-foreground-950 text-right">{experience.category}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2">
                    <i className="ri-group-line text-secondary-400" />
                    {t('experience.travelStyle')}
                  </span>
                  <span className="font-semibold text-foreground-950 text-right">{experience.group_type}</span>
                </div>
                {experience.region && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2">
                      <i className="ri-map-pin-line text-secondary-400" />
                      {t('experience.region')}
                    </span>
                    <span className="font-semibold text-foreground-950 text-right">{REGION_LABEL_KEYS[experience.region] ? t(REGION_LABEL_KEYS[experience.region]) : experience.region}</span>
                  </div>
                )}
              </div>
              <Link
                to={`/contact?tour=${encodeURIComponent(experience.title)}`}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-[10px] bg-primary-500 px-6 py-3 text-[13.5px] font-bold whitespace-nowrap text-white hover:bg-primary-600 transition-colors"
              >
                {t('experience.enquire')}
                <i className="ri-arrow-right-line" />
              </Link>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}